// frontend/src/components/CatalogShowcase.jsx
// Professional catalog showcase with secure audio streaming

import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config/api.js';
import './CatalogShowcase.css';
import { getShowcaseCatalog } from '../services/CatalogService.js';

const CatalogShowcase = () => {
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [vibeFilter, setVibeFilter] = useState('');
  const [streamingUrls, setStreamingUrls] = useState({});
  const [apiTracks, setApiTracks] = useState([]);
  const [allTracks, setAllTracks] = useState([]);

  useEffect(() => {
    fetchStreamingUrls();
    fetchApiTracks();
  }, []);

    // Fetch tracks from API via service and merge with static data
    const fetchApiTracks = async () => {
      try {
        const { tracks: apiTracks } = await getShowcaseCatalog();
        if (apiTracks.length > 0) {
          console.log('🎵 Loaded', apiTracks.length, 'tracks from API');

          // Convert API tracks to showcase format
          const convertedTracks = apiTracks.slice(0, 20).map(apiTrack => ({
            id: apiTrack.id,
            track_title: apiTrack.track_title,
            album: apiTrack.playlist ? apiTrack.playlist.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown Album',
            artist: apiTrack.artist || 'Rue De Vivre',
            bpm: apiTrack.bpm || 120,
            vibe: apiTrack.sync_category || 'Electronic vibes',
            songwriter: 'Avril Hue (IPI: 1275926811)',
            publisher: 'decoded music | AHA LLC',
            spotify_url: apiTrack.spotify_url || 'https://open.spotify.com/artist/4Z8W4fKeB5YxbusRsdQVPb',
            album_art: 'https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/okina-sakana/art/okina-cover-2.png',
            pitch_themes: [apiTrack.sync_category || 'Commercial sync', 'Brand campaigns', 'Background music'],
            lyrics_preview: `${apiTrack.track_title} - professional track with ${apiTrack.bpm} BPM`,
            preview_url: apiTrack.preview_url
          }));

          // Merge API tracks with static tracks
          setAllTracks([...tracks, ...convertedTracks]);
          console.log('🎼 Total tracks available:', tracks.length + convertedTracks.length);
        } else {
          setAllTracks(tracks);
        }
      } catch (error) {
        console.warn('⚠️ API error:', error.message);
        setAllTracks(tracks);
      }
    };
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch streaming URLs when component mounts
  useEffect(() => {
    fetchStreamingUrls();
  }, []);

  const fetchStreamingUrls = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to fetch streaming URLs, but fallback gracefully if not available
      try {
        const response = await fetch(`${API_BASE_URL}/audio/streaming?action=get-urls`);
        const data = await response.json();
        if (data.success) {
          setStreamingUrls(data.streamingUrls);
          console.log('Streaming URLs loaded:', data.streamingUrls);
        } else {
          console.log('Streaming URLs not available, using direct S3 URLs');
        }
      } catch (audioError) {
        console.log('Audio streaming API not available, using direct S3 URLs');
      }
    } catch (error) {
      console.error('Error in fetchStreamingUrls:', error);
    } finally {
      setLoading(false);
    }
  };

  // Audio playback control with fallback to direct S3 URLs
  const playTrack = (track) => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }

    const trackId = typeof track === 'string' ? track : track.id;
    
    if (isPlaying && currentlyPlaying === trackId) {
      setIsPlaying(false);
      setCurrentlyPlaying(null);
      setCurrentAudio(null);
      return;
    }

    // Get the track object to access assets
    const trackObj = allTracks.find(t => t.id === trackId);
    let audioUrl;
    
    // Use track assets if available, otherwise construct URL
    if (trackObj?.assets?.clip_wav) {
      audioUrl = trackObj.assets.clip_wav;
    } else if (trackObj?.assets?.clip_mp3) {
      audioUrl = trackObj.assets.clip_mp3;
    } else if (trackObj?.s3_url) {
      audioUrl = trackObj.s3_url;
    } else {
      // Fallback to constructed URL using correct bucket
      audioUrl = `https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/playlists/${trackId}.mp3`;
    }
    
    console.log('Using audio URL:', audioUrl);

    const audio = new Audio(audioUrl);
    audio.addEventListener('loadstart', () => console.log('Loading audio...'));
    audio.addEventListener('canplay', () => console.log('Audio ready to play'));
    audio.addEventListener('error', (e) => {
      console.error('Audio error:', e);
      // Try alternative formats from track assets
      let altUrl = null;
      if (trackObj?.assets?.clip_mp3 && trackObj.assets.clip_mp3 !== audioUrl) {
        altUrl = trackObj.assets.clip_mp3;
      } else if (trackObj?.assets?.clip_wav && trackObj.assets.clip_wav !== audioUrl) {
        altUrl = trackObj.assets.clip_wav;
      } else {
        // Fallback to different format in same bucket
        altUrl = `https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/playlists/${trackId}.wav`;
      }
      
      if (altUrl) {
        const altAudio = new Audio(altUrl);
        altAudio.play().then(() => {
          setCurrentAudio(altAudio);
          setIsPlaying(true);
          setCurrentlyPlaying(trackId);
          setSelectedTrack(trackId);
        }).catch(err => {
          console.error('Alternative audio also failed:', err);
        });
      }
    });
    
    audio.play().then(() => {
      setCurrentAudio(audio);
      setIsPlaying(true);
      setCurrentlyPlaying(trackId);
      setSelectedTrack(trackId);
    }).catch(error => {
      console.error('Error playing audio:', error);
    });

    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setCurrentAudio(null);
      setCurrentlyPlaying(null);
    });
  };

  // Updated catalog data with secure audio streaming
  const tracks = [
    {
      id: "okina-01",
      track_title: "999,999",
      album: "Okina Sakana",
      artist: "Rue De Vivre",
      bpm: 128,
      vibe: "Money moves and side-hustle grind",
      songwriter: "Avril Hue (IPI: 1275926811)",
      publisher: "Publisher (IPI: 1275926713)",
      spotify_id: "0jGJEdoVlvOWVt3C9GcFJQ",
      spotify_url: "https://open.spotify.com/track/0jGJEdoVlvOWVt3C9GcFJQ",
      album_art: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/okina-sakana/art/okina-cover-2.png",
      pitch_themes: ["Productivity apps", "crypto/fintech commercials", "office-comedy ads", "SaaS or startup tech"],
      lyrics_preview: "Nine nine nine, nine nine nine, Money moves on my mind"
    },
    {
      id: "okina-02", 
      track_title: "OG Bashment",
      album: "Okina Sakana",
      artist: "Rue De Vivre",
      bpm: 135,
      vibe: "Global block-party celebration",
      songwriter: "Avril Hue",
      spotify_id: "5LsoV9QPzdaZ1T3U3F7pFd",
      spotify_url: "https://open.spotify.com/track/5LsoV9QPzdaZ1T3U3F7pFd",
      album_art: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/okina-sakana/art/okina-cover-2.png",
      pitch_themes: ["International campaigns", "dancehall-inspired brand spots", "global streaming platforms", "Caribbean tourism"],
      lyrics_preview: "Original gangster bashment style, Global vibes make the crowd go wild"
    },
    {
      id: "okina-03",
      track_title: "Monday", 
      album: "Okina Sakana",
      artist: "Rue De Vivre",
      bpm: 102,
      vibe: "Back-to-work Monday energy",
      songwriter: "Avril Hue (IPI: 1275926811)",
      spotify_id: "16y1FUke3lRQi28TZQxd1C",
      spotify_url: "https://open.spotify.com/track/16y1FUke3lRQi28TZQxd1C",
      album_art: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/okina-sakana/art/okina-cover-2.png",
      pitch_themes: ["Monday motivation campaigns", "coffee spots", "productivity apps", "workplace wellness"],
      lyrics_preview: "Monday morning, here we go again, Ready to win, let the work begin"
    },
    {
      id: "okina-06",
      track_title: "Okina Sakana",
      album: "Okina Sakana",
      artist: "Rue De Vivre",
      bpm: 128,
      vibe: "Album title track energy",
      songwriter: "Avril Hue",
      spotify_id: "1FLnKChImgS6v8EpYZaAkp",
      spotify_url: "https://open.spotify.com/track/1FLnKChImgS6v8EpYZaAkp",
      album_art: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/okina-sakana/art/okina-cover-2.png",
      pitch_themes: ["Cultural fusion brands", "Japanese-inspired campaigns", "seafood restaurants", "aquarium experiences"],
      lyrics_preview: "Okina Sakana, swimming free, In the ocean of possibility"
    },
    {
      id: "okina-07",
      track_title: "Friday Flex",
      album: "Okina Sakana",
      artist: "Rue De Vivre",
      bpm: 135,
      vibe: "Payday victory clock-out party",
      songwriter: "Avril Hue",
      spotify_id: "6rsUqByoABGVPOiWOFpY0o",
      spotify_url: "https://open.spotify.com/track/6rsUqByoABGVPOiWOFpY0o",
      album_art: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/okina-sakana/art/okina-cover-2.png",
      pitch_themes: ["Weekend campaigns", "payroll services", "Friday night events", "work-life balance apps"],
      lyrics_preview: "Friday flex, time to rest, Weekend vibes, we are blessed"
    },
    {
      id: "okina-08",
      track_title: "WEED",
      album: "Okina Sakana",
      artist: "Rue De Vivre",
      bpm: 145,
      vibe: "Herbal chill wellness pride",
      songwriter: "Avril Hue",
      spotify_id: "1YLXMtRGkSij2yp6lmIjyS",
      spotify_url: "https://open.spotify.com/track/1YLXMtRGkSij2yp6lmIjyS",
      album_art: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/okina-sakana/art/okina-cover-2.png",
      pitch_themes: ["Cannabis wellness", "herbal products", "chill lifestyle brands", "natural remedies"],
      lyrics_preview: "Herb the verb, natural high, Cannabis culture reaching sky"
    },
    {
      id: "okina-04",
      track_title: "Big Fish (Okina Sakana)",
      album: "Okina Sakana",
      artist: "Rue De Vivre",
      bpm: 128,
      vibe: "Ocean boss confidence anthem",
      songwriter: "Avril Hue",
      spotify_id: "1FLnKChImgS6v8EpYZaAkp",
      spotify_url: "https://open.spotify.com/track/1FLnKChImgS6v8EpYZaAkp",
      album_art: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/okina-sakana/art/okina-cover-2.png",
      pitch_themes: ["Leadership campaigns", "aquaculture brands", "corporate confidence", "ocean conservation"],
      lyrics_preview: "Big fish in the ocean wide, Swimming with that boss pride"
    },
    {
      id: "okina-05",
      track_title: "Shine Every Time (Carpe Diem)",
      album: "Okina Sakana",
      artist: "Rue De Vivre",
      bpm: 125,
      vibe: "Seize the day motivation anthem",
      songwriter: "Avril Hue",
      spotify_id: "3kPvUhSLWJp6ZKNj0R2i4p",
      spotify_url: "https://open.spotify.com/track/3kPvUhSLWJp6ZKNj0R2i4p",
      album_art: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/okina-sakana/art/okina-cover-2.png",
      pitch_themes: ["Motivational campaigns", "life coaching", "personal development", "wellness brands"],
      lyrics_preview: "Shine every time, carpe diem, Live your life like you mean it"
    },
    // New Album: Reclaim
    {
      id: "reclaim-01",
      track_title: "Reclaim (Track 1)",
      album: "Reclaim",
      artist: "Rue De Vivre",
      bpm: 120,
      vibe: "Empowerment and self-discovery",
      songwriter: "Avril Hue (IPI: 1275926811)",
      publisher: "Publisher (IPI: 1275926713)",
      spotify_album_id: "3mza84ri2Y2GqRLpdYqMjE",
      spotify_url: "https://open.spotify.com/album/3mza84ri2Y2GqRLpdYqMjE",
      album_art: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/reclaim/art/reclaim-cover.png",
      pitch_themes: ["Self-empowerment campaigns", "new beginnings", "transformation themes", "lifestyle brands"],
      lyrics_preview: "Time to reclaim what's mine, take back control"
    },
    {
      id: "reclaim-02",
      track_title: "Reclaim (Track 2)",
      album: "Reclaim",
      artist: "Rue De Vivre",
      bpm: 128,
      vibe: "Confidence and determination",
      songwriter: "Avril Hue (IPI: 1275926811)",
      publisher: "Publisher (IPI: 1275926713)",
      spotify_album_id: "3mza84ri2Y2GqRLpdYqMjE",
      spotify_url: "https://open.spotify.com/album/3mza84ri2Y2GqRLpdYqMjE",
      album_art: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/reclaim/art/reclaim-cover.png",
      pitch_themes: ["Corporate empowerment", "success stories", "achievement campaigns", "motivation"],
      lyrics_preview: "Rising up stronger than before, this is my moment"
    },
    {
      id: "reclaim-03",
      track_title: "Reclaim (Track 3)",
      album: "Reclaim",
      artist: "Rue De Vivre",
      bpm: 132,
      vibe: "High-energy breakthrough",
      songwriter: "Avril Hue (IPI: 1275926811)",
      publisher: "Publisher (IPI: 1275926713)",
      spotify_album_id: "3mza84ri2Y2GqRLpdYqMjE",
      spotify_url: "https://open.spotify.com/album/3mza84ri2Y2GqRLpdYqMjE",
      album_art: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/reclaim/art/reclaim-cover.png",
      pitch_themes: ["Sports campaigns", "fitness motivation", "energy drinks", "athletic wear"],
      lyrics_preview: "Breaking through barriers, unstoppable force"
    }
  ];

  // Filter tracks based on search and vibe (use allTracks which includes API data)
  const filteredTracks = allTracks.filter(track => {
    const matchesSearch = !searchFilter || 
      track.track_title.toLowerCase().includes(searchFilter.toLowerCase()) ||
      track.vibe.toLowerCase().includes(searchFilter.toLowerCase()) ||
      track.pitch_themes.some(theme => theme.toLowerCase().includes(searchFilter.toLowerCase()));
    
    const matchesVibe = !vibeFilter || track.vibe.toLowerCase().includes(vibeFilter.toLowerCase());
    
    return matchesSearch && matchesVibe;
  });

  return (
    <div className="catalog-showcase">
      <div className="showcase-header">
        <h1>🎵 DecodedMusic Catalog Showcase</h1>
        <p className="showcase-subtitle">Professional music catalog with secure streaming previews</p>
        
        {loading && (
          <div className="loading-indicator">
            <div className="loading-spinner"></div>
            <p>Loading catalog...</p>
          </div>
        )}

        {error && (
          <div className="error-indicator">
            <p>⚠️ {error}</p>
            <button onClick={fetchStreamingUrls} className="retry-button">
              Retry Loading
            </button>
          </div>
        )}
        
        {!loading && !error && (
          <div className="catalog-loaded">
            <p className="status-message">✅ Catalog loaded successfully • {filteredTracks.length} tracks available</p>
          </div>
        )}
      </div>

      {/* Track Grid */}
      {!loading && !error && (
        <div className="tracks-grid">
          {filteredTracks.map(track => (
          <div
            key={track.id}
            className={`track-card ${currentlyPlaying === track.id ? 'playing' : ''}`}
          >
            <div className="track-header">
              <img 
                src={track.album_art} 
                alt={`${track.album} cover`}
                className="album-art"
                onError={(e) => {
                  e.target.src = '/logo.svg';
                }}
              />
              <div className="track-info">
                <h3 className="track-title">{track.track_title}</h3>
                <p className="artist-name">{track.artist}</p>
                <p className="album-name">{track.album}</p>
              </div>
            </div>

            {/* Audio Controls */}
            <div className="audio-controls">
              <button
                onClick={() => playTrack(track)}
                className={`play-button ${currentlyPlaying === track.id && isPlaying ? 'playing' : ''}`}
              >
                {currentlyPlaying === track.id && isPlaying ? '⏸️ Pause' : '▶️ Play Preview'}
              </button>
            </div>

            {/* Track Details */}
            <div className="track-details">
              <div className="track-meta">
                <span className="bpm">🎵 {track.bpm} BPM</span>
                <span className="vibe">✨ {track.vibe}</span>
              </div>
              
              <div className="lyrics-preview">
                <strong>Lyrics Preview:</strong>
                <em>"{track.lyrics_preview}"</em>
              </div>

              <div className="pitch-themes">
                <strong>Perfect for:</strong>
                <div className="themes-tags">
                  {track.pitch_themes.map((theme, idx) => (
                    <span key={idx} className="theme-tag">{theme}</span>
                  ))}
                </div>
              </div>

              <div className="credits">
                <p><strong>Songwriter:</strong> {track.songwriter}</p>
                {track.publisher && <p><strong>Publisher:</strong> {track.publisher}</p>}
              </div>

              {/* Spotify Link */}
              {track.spotify_url && (
                <div className="spotify-link">
                  <a 
                    href={track.spotify_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="spotify-button"
                  >
                    🎧 Listen on Spotify
                  </a>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Streaming Status */}
      <div className="streaming-info">
        <p>🔒 Audio streaming available • 🎧 Preview clips • ✨ Full tracks on Spotify</p>
        <p>✅ {filteredTracks.length} tracks ready to play</p>
      </div>

      {/* Search and Filter Controls - Moved to Bottom */}
      {!loading && !error && (
        <div className="showcase-controls">
          <h3>🔍 Search & Filter</h3>
          <div className="controls-grid">
            <input
              type="text"
              placeholder="Search tracks, vibes, or themes..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="search-input"
            />
            <select
              value={vibeFilter}
              onChange={(e) => setVibeFilter(e.target.value)}
              className="vibe-filter"
            >
              <option value="">All Vibes</option>
              <option value="money">Money/Business</option>
              <option value="party">Party/Celebration</option>
              <option value="monday">Monday/Work</option>
              <option value="weekend">Weekend/Friday</option>
              <option value="wellness">Wellness/Chill</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogShowcase;
